import React, { useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Droplet, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { useI18n } from "../i18n";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Onboarding({ initial, onSubmit }) {
  const { t, dateLocale } = useI18n();
  const [lastPeriod, setLastPeriod] = useState(
    initial?.lastPeriod ? new Date(initial.lastPeriod + "T00:00:00") : undefined
  );
  const [periodLength, setPeriodLength] = useState(initial?.periodLength || 5);
  const [cycleLength, setCycleLength] = useState(initial?.cycleLength || 28);
  const [weight, setWeight] = useState(initial?.weight || "");
  const [open, setOpen] = useState(false);

  const valid = lastPeriod && periodLength > 0 && cycleLength > 0 && Number(weight) > 0;

  const submit = () => {
    if (!valid) return;
    onSubmit({
      lastPeriod: format(lastPeriod, "yyyy-MM-dd"),
      periodLength: Number(periodLength),
      cycleLength: Number(cycleLength),
      weight: Number(weight),
    });
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 grain-bg">
      <div
        data-testid="onboarding-card"
        className="relative z-10 w-full max-w-md bg-bone border border-border rounded-3xl p-7 sm:p-9 animate-fade-up shadow-[0_1px_0_rgba(0,0,0,0.02)]"
      >
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase font-bold text-primary">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" /> Cycle IQ
            </div>
            <LanguageSwitcher />
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl leading-[0.95] text-foreground">
            {t("heroLine1")}
            <br />
            <span className="text-primary italic">{t("heroLine2")}</span>
          </h1>
          <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
            {t("heroSub")}
          </p>
        </div>

        <div className="space-y-5">
          <div>
            <Label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">
              {t("lastPeriodLabel")}
            </Label>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="date-picker-trigger"
                  className="mt-2 w-full justify-start h-12 rounded-xl border-border bg-white text-left font-normal hover:bg-secondary"
                >
                  <CalendarIcon className="mr-2 h-4 w-4 text-primary" />
                  {lastPeriod ? format(lastPeriod, "MMMM d, yyyy", { locale: dateLocale }) : t("selectDate")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={lastPeriod}
                  onSelect={(d) => {
                    setLastPeriod(d);
                    setOpen(false);
                  }}
                  disabled={(date) => date > new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label
                htmlFor="period-length"
                className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground"
              >
                {t("periodLengthLabel")}
              </Label>
              <div className="relative mt-2">
                <Input
                  id="period-length"
                  data-testid="period-length-input"
                  type="number"
                  min="1"
                  max="14"
                  value={periodLength}
                  onChange={(e) => setPeriodLength(e.target.value)}
                  className="h-12 rounded-xl bg-white pr-12"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  {t("days")}
                </span>
              </div>
            </div>
            <div>
              <Label
                htmlFor="cycle-length"
                className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground"
              >
                {t("cycleLengthLabel")}
              </Label>
              <div className="relative mt-2">
                <Input
                  id="cycle-length"
                  data-testid="cycle-length-input"
                  type="number"
                  min="20"
                  max="45"
                  value={cycleLength}
                  onChange={(e) => setCycleLength(e.target.value)}
                  className="h-12 rounded-xl bg-white pr-12"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  {t("days")}
                </span>
              </div>
            </div>
          </div>

          <div>
            <Label
              htmlFor="weight"
              className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground"
            >
              {t("weightLabel")}
            </Label>
            <div className="relative mt-2">
              <Input
                id="weight"
                data-testid="weight-input"
                type="number"
                min="1"
                placeholder={t("weightPlaceholder")}
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                className="h-12 rounded-xl bg-white pr-12"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                {t("lb")}
              </span>
            </div>
            <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Droplet className="h-3 w-3 text-phase-follicular" />
              {t("weightHint")}
            </p>
          </div>

          <Button
            data-testid="onboarding-submit-btn"
            onClick={submit}
            disabled={!valid}
            className="w-full h-13 py-6 rounded-full bg-primary text-primary-foreground text-base font-semibold hover:bg-primary/90 hover:-translate-y-0.5 active:scale-[0.98] transition-transform disabled:opacity-40 disabled:hover:translate-y-0"
          >
            {t("mapCycle")}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
